import { useState, useCallback } from "react";

export interface UploadResult {
  url: string;
  key: string;
  originalName: string;
  size: number;
  type: string;
}

export interface UseR2UploadReturn {
  upload: (file: File, folder?: string) => Promise<UploadResult>;
  uploading: boolean;
  progress: number;
  error: string | null;
}

/**
 * Hook para upload de arquivos ao Cloudflare R2 via Supabase Edge Function.
 * O arquivo é enviado para R2 e apenas a URL é retornada.
 *
 * @returns {UseR2UploadReturn} upload function, loading state, progress e error
 *
 * @example
 * const { upload, uploading, progress, error } = useR2Upload();
 * const result = await upload(file, "avatars");
 * console.log(result.url); // URL pública do arquivo no R2
 */
export function useR2Upload(): UseR2UploadReturn {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);

  // URL fixa do Supabase pessoal do usuário (projeto definitivo)
  const EDGE_FUNCTION_URL = "https://egszaqptmlunwcfujxxp.supabase.co/functions/v1/upload-to-r2";

  const upload = useCallback(
    async (file: File, folder = "uploads"): Promise<UploadResult> => {
      setUploading(true);
      setProgress(0);
      setError(null);

      try {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("folder", folder);

        setProgress(30);

        const response = await fetch(EDGE_FUNCTION_URL, {
          method: "POST",
          body: formData,
          headers: {
            "apikey": import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY || "",
            "Authorization": `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY || ""}`,
          },
        });

        setProgress(70);

        if (!response.ok) {
          let errData: Record<string, string> | null = null;
          try {
            errData = await response.json();
          } catch {
            errData = null;
          }
          throw new Error(errData?.error || `Upload failed: ${response.status}`);
        }

        const data = await response.json();
        setProgress(100);

        if (!data.success || !data.url) {
          throw new Error("Invalid response from upload server");
        }

        return {
          url: data.url,
          key: data.key,
          originalName: data.originalName,
          size: data.size,
          type: data.type,
        };
      } catch (err) {
        const message = err instanceof Error ? err.message : "Upload failed";
        setError(message);
        throw new Error(message);
      } finally {
        setUploading(false);
      }
    },
    []
  );

  return { upload, uploading, progress, error };
}